import { useContext, useEffect, useState } from "react";
import { Chart } from "react-google-charts";
import { ThemeContext } from "../../../../theme/ThemeContext";
import AuthContext from "../../../../auth/AuthContext";
import { Skeleton } from "primereact/skeleton";
import TicketDashboardService from "../../../../services/TicketDashboardService";
import { useResizeDetector } from "react-resize-detector";

export default function LearnerTicketStatusPieChart() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);

  const [ticketData, setTicketData] = useState([["Status", "Count"]]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [chartKey, setChartKey] = useState(0);

  const { ref, width, height } = useResizeDetector();

  useEffect(() => {
    if (width && height) {
      setChartKey((prev) => prev + 1);
    }
  }, [width, height]);


useEffect(() => {
    const controller = new AbortController();

    const fetchTicketData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await TicketDashboardService.GetUserDashboard(
          authContext,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (!response) return;
        if (
          response.isSuccess &&
          response.data &&
          typeof response.data === "object" &&
          !Array.isArray(response.data)
        ) {
          const obj = response.data;
          const ticketEntries = Object.entries(obj).filter(
            ([key]) => key !== "totalTickets"
          );
          const formattedData = [
            ["Status", "Count"],
            ...ticketEntries
              .map(([k, v]) => [
                k.charAt(0).toUpperCase() + k.slice(1),
                v,
              ])
              .filter(([, value]) => value > 0) // Only > 0 values
          ];
          setTicketData(formattedData);
        } else {
          setError(
            "Failed to fetch tickets: " + (response.message || "Unknown error")
          );
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to fetch tickets: " + err.message);
        console.error("Ticket API error:", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchTicketData();

    return () => controller.abort();
  }, [authContext]);

  const totalTickets =
    ticketData.length > 1
      ? ticketData.slice(1).reduce((sum, [, count]) => sum + count, 0)
      : 0;

  const colors = ["#f4511e", "#fb8c00", "#1e88e5", "#e53935"];

  const options = {
    backgroundColor: "transparent",
    pieHole: 0.4,
    is3D: true,
    pieStartAngle: 100,
    sliceVisibilityThreshold: 0,

    chartArea: {
      width: "75%",
      height: "80%",
      left: "12%", 
      top: "5%",
    },

    legend: {
      position: "bottom",
      alignment: "center",
      textStyle: {
        fontSize: 11,
        color: isDark ? "#eaeaea" : "#000",
        fontName: "Poppins",
      },
    },

    tooltip: {
      isHtml: false,
      textStyle: {
        fontSize: 10,
        fontName: "Poppins",
      },
      showColorCode: true,
    },

    colors,
  };

  const chartEvents = [
    {
      eventName: "select",
      callback: ({ chartWrapper }) => {
        const chart = chartWrapper.getChart();
        const selection = chart.getSelection();
        if (selection.length > 0) {
          const rowIndex = selection[0].row;
          const value = ticketData[rowIndex + 1]?.[1];
          if (value === 0) {
            chart.setSelection([]);
          }
        }
      },
    },
  ];

  if (loading) {
    return (
      <div className="charts" style={{ height: "357px", padding: "14px" }}>
        <Skeleton width="120px" height="20px" />
        <Skeleton width="100px" height="16px" className="mt-2" />
        <div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
          <Skeleton shape="circle" size="200px" />
        </div>
      </div>
    );
  }

  if (error) {
    return <div style={{ padding: "14px", color: "red" }}>{error}</div>;
  }

  return (
    <div
      className="charts"
      style={{
        width: "100%",
        height: "357px",
        padding: "14px",
        borderRadius: "12px",
      }}
    >
      {/* Header */}
      <div className="d-flex justify-content-between">
        <div>
          <div style={{ fontSize: 16 }}>Tickets Status</div>
          <p
            style={{
              fontSize: "13px",
              color: isDark ? "#eaeaea" : "#6c757d",
            }}
          >
            Total Tickets: <b>{totalTickets}</b>
          </p>
        </div>
      </div>

      {/* Chart */}
      <div
        ref={ref}
        style={{
          width: "100%",
          height: "calc(100% - 80px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        {totalTickets === 0 ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: isDark ? "#aaa" : "#666",
            }}
          >
            No data available
          </div>
        ) : (
          <Chart
            key={chartKey} 
            chartType="PieChart"
            data={ticketData}
            options={options}
            width="100%"
            height="100%"
            chartEvents={chartEvents}
          />
        )}
      </div>
    </div>
  );
}